<template>
  <div>
    <v-autocomplete
      v-model="localValue"
      :items="categoryOptions"
      :loading="isLoading"
      item-text="label"
      item-value="id"
      :label="label || $t('STOCK.PRODUCTS.CATEGORY') + '*'"
      :disabled="disabled || !departmentId"
      clearable
      :search-input.sync="search"
      :error-messages="errorMessages"
      @change="emitChange"
      @update:search-input="handleSearch"
    >
      <template v-slot:append-outer>
        <v-btn
          icon
          color="primary"
          :disabled="disabled || !departmentId"
          @click="showCreateDialog = true"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </template>
    </v-autocomplete>

    <v-dialog v-model="showCreateDialog" max-width="460">
      <v-card>
        <v-card-title>{{ $t("STOCK.PRODUCTS.CREATE_CATEGORY") }}</v-card-title>
        <v-card-text>
          <v-form ref="categoryForm" v-model="validCategoryForm" @submit.prevent>
            <v-text-field
              ref="newCategoryNameInput"
              v-model="newCategory.name"
              :label="$t('STOCK.PRODUCTS.CATEGORY_NAME') + '*'"
              :rules="[validations.required()]"
              :error-messages="validationErrors.name || []"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">
            {{ $t("GENERAL.LABELS.CLOSE_BUTTON") }}
          </v-btn>
          <v-btn text color="primary" :loading="isCreating" @click="createCategory">
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from "lodash";
import validations from "@/views/partials/form/Validations.vue";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  CREATE_STOCK_PRODUCT_CATEGORY,
  GET_STOCK_PRODUCT_CATEGORY_OPTIONS,
} from "@/store/stock/stockProductCategories.module";

export default {
  name: "StockProductCategoryPicker",
  props: {
    value: {
      default: null,
    },
    initialCategory: {
      type: Object,
      default: null,
    },
    departmentId: {
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      validations,
      localValue: this.value,
      categoryOptions: [],
      search: "",
      isLoading: false,
      isCreating: false,
      showCreateDialog: false,
      validCategoryForm: false,
      validationErrors: {},
      newCategory: {
        name: "",
      },
    };
  },
  watch: {
    value(value) {
      this.localValue = value;
    },
    initialCategory: {
      handler(category) {
        this.mergeCategoryOptions(category ? [category] : []);
      },
      deep: true,
      immediate: true,
    },
    departmentId(value, previous) {
      if (value === previous) {
        return;
      }
      if (!value) {
        this.categoryOptions = [];
        return;
      }
      this.loadCategories(this.search || "");
    },
    showCreateDialog(value) {
      if (value) {
        this.$nextTick(() => {
          setTimeout(() => this.$refs.newCategoryNameInput?.focus(), 100);
        });
      }
    },
  },
  mounted() {
    if (this.departmentId) {
      this.loadCategories("");
    }
  },
  methods: {
    handleSearch: debounce(function (search) {
      if (!this.departmentId) {
        return;
      }
      this.loadCategories(search || "");
    }, 350),
    loadCategories(search) {
      if (!this.departmentId) {
        this.categoryOptions = [];
        return Promise.resolve();
      }

      this.isLoading = true;
      return this.$store
        .dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {
          search,
          stock_product_department_id: this.departmentId,
        })
        .then((response) => {
          this.categoryOptions = response.data || [];
          this.mergeCategoryOptions(this.initialCategory ? [this.initialCategory] : []);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    mergeCategoryOptions(categories) {
      const byId = {};
      [...this.categoryOptions, ...(categories || [])].forEach((category) => {
        if (category && category.id) {
          byId[category.id] = category;
        }
      });
      this.categoryOptions = Object.values(byId);
    },
    emitChange() {
      const selected =
        this.categoryOptions.find(
          (category) => Number(category.id) === Number(this.localValue)
        ) || null;
      this.$emit("input", this.localValue);
      this.$emit("change", selected);
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.validationErrors = {};
      this.newCategory = {
        name: "",
      };
    },
    createCategory() {
      this.$refs.categoryForm.validate();
      if (!this.validCategoryForm) {
        return;
      }

      if (!this.departmentId) {
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.VALIDATION.REQUIRED_FIELD"),
          type: "error",
        });
        return;
      }

      this.validationErrors = {};
      this.isCreating = true;

      this.$store
        .dispatch(CREATE_STOCK_PRODUCT_CATEGORY, {
          name: this.newCategory.name,
          stock_product_department_id: this.departmentId,
          is_active: true,
        })
        .then((category) => {
          this.mergeCategoryOptions([category]);
          this.localValue = category.id;
          this.emitChange();
          this.closeCreateDialog();
        })
        .catch((error) => {
          this.validationErrors = error?.errors || {};
          this.$store.dispatch(SHOW_SNACK, {
            message: error?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isCreating = false;
        });
    },
  },
};
</script>
